import { useStressTestStore } from '../../store/stressTestStore';
import { PrerequisitesSection } from './PrerequisitesSection';
import { BaseCaseCard } from './BaseCaseCard';
import { ResultsSummary } from './ResultsSummary';
import { ImpactsList } from './ImpactsList';
import { RunHistoryCard } from './RunHistoryCard';
import { RolloutCard } from './RolloutCard';
import { RolloutResults } from './RolloutResults';
import { MeasuresCard } from './MeasuresCard';
import { RemedyCard } from './RemedyCard';
import { UnitCard } from './UnitCard';

interface StressTestSidebarProps {
  onZoneChange?: (zoneId: string) => void;
  onRunStressTest?: () => void;
}

/**
 * Left panel of the Stressszenarien page. Two modes: a single what-if run
 * (Szenario) on one archived frame, or a Hochlaufpfad (Stützjahre × Monte
 * Carlo on both worst frames). Grid changes (Zubau, neue Hausanschlüsse) are
 * made on the map through the element editor of the right sidebar.
 */
export function StressTestSidebar({ onZoneChange, onRunStressTest }: StressTestSidebarProps) {
  const stressZoneId = useStressTestStore(s => s.stressZoneId);
  const isCalculating = useStressTestStore(s => s.isCalculating);
  const run = useStressTestStore(s => s.run);
  const rollout = useStressTestStore(s => s.rollout);
  const rolloutJob = useStressTestStore(s => s.rolloutJob);
  const calculationError = useStressTestStore(s => s.calculationError);
  const runStressTest = useStressTestStore(s => s.runStressTest);
  const startRollout = useStressTestStore(s => s.startRollout);
  const baseCase = useStressTestStore(s => s.baseCase);
  const planningMode = useStressTestStore(s => s.planningMode);
  const setPlanningMode = useStressTestStore(s => s.setPlanningMode);
  const rolloutPath = useStressTestStore(s => s.rolloutPath);

  const isRollout = planningMode === 'rollout';
  const hasResults = isRollout ? rollout != null : run != null;
  const timestampMissing = !isRollout && baseCase.mode === 'archive' && baseCase.frame === 'timestamp' && !baseCase.timestamp;
  const pathMissing = isRollout && rolloutPath.length === 0;
  const disabled = isCalculating || !stressZoneId || timestampMissing || pathMissing;

  const handleRun = async () => {
    if (!stressZoneId) return;
    if (isRollout) {
      await startRollout();
    } else {
      await runStressTest(stressZoneId);
    }
    onRunStressTest?.();
  };

  return (
    <div className="h-full bg-white dark:bg-primary-800 border-r border-gray-300 dark:border-primary-500 shadow-lg flex flex-col transition-all duration-300 w-80" id="stress-test-sidebar">
      {/* Spacer for logo overlay */}
      <div className="min-h-[56px] shrink-0" />
      <div className="shrink-0">
        <PrerequisitesSection onZoneChange={onZoneChange} />
        {/* Mode */}
        <div className="px-3 pt-2 pb-1">
          <div className="flex rounded-lg border border-gray-300 dark:border-primary-500 overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setPlanningMode('scenario')}
              className={`flex-1 px-2 py-1.5 font-medium transition-colors ${!isRollout ? 'bg-gray-800 text-white dark:bg-primary-500' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-600'}`}
            >
              Einzelszenario
            </button>
            <button
              type="button"
              onClick={() => setPlanningMode('rollout')}
              className={`flex-1 px-2 py-1.5 font-medium transition-colors ${isRollout ? 'bg-gray-800 text-white dark:bg-primary-500' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-600'}`}
            >
              Hochlaufpfad
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {isRollout ? <RolloutResults /> : <ResultsSummary />}

        {/* A failed rollout job is shown by RolloutResults in place of its progress card. */}
        {calculationError && !(isRollout && rolloutJob?.state === 'failed') && (
          <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-xs text-red-700 dark:text-red-400">{calculationError}</p>
          </div>
        )}

        {!isRollout && hasResults && <RemedyCard />}
        {!isRollout && hasResults && <ImpactsList />}

        <MeasuresCard />

        <UnitCard />

        {isRollout ? <RolloutCard hasResults={hasResults} /> : <BaseCaseCard hasResults={hasResults} />}

        <p className="text-[11px] text-gray-500 dark:text-primary-400 px-1 leading-snug">
          Zubau (PV, Wallbox, Wärmepumpe), neue Hausanschlüsse und Maßnahmen (Kabel und Sicherung tauschen, Trafo tauschen, Stufenschalter/rONT) werden direkt auf der Karte am jeweiligen Element eingetragen und mit dem Szenario gespeichert{isRollout ? '; der Hochlauf setzt darauf auf' : ''}. Gekoppelte Nachbarzonen (Netzverbund) werden immer mitgerechnet, liegen als Blasen auf der Karte und lassen sich einzeln dazuschalten; Trennstellen lassen sich im Szenario öffnen und schließen.
        </p>

        <RunHistoryCard />
      </div>

      <div className="shrink-0 px-3 py-3 border-t border-gray-200 dark:border-primary-600">
        {!stressZoneId && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-2 text-center">Bitte wählen Sie eine Zone aus.</p>
        )}
        {timestampMissing && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-2 text-center">Bitte einen Zeitpunkt wählen.</p>
        )}
        {pathMissing && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mb-2 text-center">Bitte mindestens ein Stützjahr angeben.</p>
        )}
        <button
          onClick={handleRun}
          disabled={disabled}
          className={`w-full px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm focus:outline-hidden focus:ring-2 focus:ring-accent-primary flex items-center justify-center gap-2 ${
            disabled
              ? 'bg-gray-400 dark:bg-primary-600 text-white cursor-not-allowed'
              : 'bg-accent-primary hover:bg-accent-primary-hover text-white border border-accent-primary'
          }`}
        >
          {isCalculating ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Berechnung...</span>
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>{isRollout ? 'Hochlauf rechnen' : 'Szenario rechnen'}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
